import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { type ApiError } from '@vello/api-client';
import { useRouter } from '../router';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { AuthLayout } from '../components/layout';
import { Button, Notice, AuthCard, Cluster, AuthTextField, FormStack, Text } from '@vello/web-theme';

/** A pragmatic email-shape check to gate the send button; the server remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


/** Map a recovery API error to a message id; the caller resolves it through the active locale. */
function recoveryErrorId(error: ApiError): string {
  switch (error.code) {
    case 'RATE_LIMITED':
      return 'login.error.rateLimited';
    case 'NETWORK':
    case 'TIMEOUT':
      return 'login.error.network';
    default:
      return 'login.error.default';
  }
}

/**
 * Operator account recovery — initiate (S111). The operator enters their email; the server starts an
 * out-of-band recovery (a verified reset link delivered to the registered channel) and, anti-enumeration,
 * always answers "accepted" — so this screen never reveals whether an account exists. The reset itself is
 * completed on `RecoveryResetScreen` via the emailed token. Shares the light auth card (design A01).
 */
export function RecoveryScreen(): ReactElement {
  const { navigate } = useRouter();
  const { runtime } = useAdmin();
  const { t } = useT();
  const emailId = useId();

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorId, setErrorId] = useState<string | undefined>(undefined);

  const canSubmit = EMAIL_RE.test(email.trim()) && !busy;

  const submit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return;
    setErrorId(undefined);
    setBusy(true);
    const result = await runtime.operatorAuth.recover(email.trim());
    setBusy(false);
    if (result.ok) setSent(true);
    else setErrorId(recoveryErrorId(result.error));
  };

  return (
    <AuthLayout>
      <AuthCard>
        <h1 className="vello-auth-heading__title">{t('recovery.title')}</h1>
        <Text as="p" tone="secondary">
          {sent ? t('recovery.sent.subtitle') : t('recovery.subtitle')}
        </Text>

        {sent ? (
          <>
            <Notice tone="success">{t('recovery.sent')}</Notice>
            <Button variant="primary" block onClick={() => navigate('login')} size="lg">
              {t('recovery.back')}
            </Button>
          </>
        ) : (
          <>
            <FormStack gap={5} onSubmit={(e) => void submit(e)} noValidate>
              <AuthTextField
                id={emailId}
                label={t('login.field.email')}
                icon="mail"
                type="email"
                name="email"
                autoComplete="username"
                placeholder={t('login.field.email.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              {errorId !== undefined && <Notice tone="danger">{t(errorId)}</Notice>}

              <Button type="submit" variant="primary" block disabled={!canSubmit} size="lg">
                {busy ? t('recovery.send.busy') : t('recovery.send')}
              </Button>
            </FormStack>

            <Cluster justify="center">
              <button type="button" className="vello-link vello-ab-linkbtn" onClick={() => navigate('login')}>
                {t('recovery.back')}
              </button>
            </Cluster>
          </>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
